import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { ArrowLeft, TrendingUp, Target, Award, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useProspects } from "../../context/ProspectsContext";

const COLORS = ["#E2231A", "#1A1A1A", "#22C55E", "#3B82F6", "#F97316", "#EAB308", "#A855F7", "#525252"];

export default function Performance() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { prospects } = useProspects();

  // Hitung dari data real
  const closingMonth = prospects.filter((p) => p.status === "closing").length;
  const totalProspects = prospects.length;
  const conversionRate = totalProspects > 0 ? Math.round((closingMonth / totalProspects) * 100) : 0;

  // Distribusi produk dari data real
  const productCounts = {};
  prospects.forEach((p) => {
    const prod = p.interested_product || "Lainnya";
    productCounts[prod] = (productCounts[prod] || 0) + 1;
  });
  const productDistribution = Object.entries(productCounts)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  // Leaderboard dari prospects (jika admin) — untuk sales hanya tampil data sendiri
  const myRank = 1;

  return (
    <div data-testid="performance-page" className="pb-4">
      {/* Header */}
      <div className="bg-honda-ink text-white px-5 pt-12 pb-8">
        <div className="flex items-center gap-3">
          <button
            data-testid="performance-back-button"
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="font-heading font-bold text-xl leading-tight">Performa Saya</h1>
            <p className="text-xs text-white/70">Statistik & ranking</p>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-3 gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">
              Konversi
            </p>
            <p className="font-heading font-extrabold text-2xl mt-1">
              {conversionRate}%
            </p>
          </div>
          <div className="border-x border-white/10 px-3">
            <p className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">
              Ranking
            </p>
            <p className="font-heading font-extrabold text-2xl mt-1">#{myRank}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">
              Coverage
            </p>
            <p className="font-heading font-extrabold text-2xl mt-1">
              {closingMonth > 0 ? `${Math.round((closingMonth / totalProspects) * 100)}%` : "0%"}
            </p>
          </div>
        </div>
      </div>

      {/* Weekly trend */}
      <div className="px-5 -mt-4">
        <div className="honda-card p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="text-honda-red" size={16} />
              <p className="font-heading font-semibold text-sm text-honda-ink">
                Tren Mingguan
              </p>
            </div>
            <span className="text-[10px] uppercase tracking-wider text-honda-subtle font-semibold">
              7 hari
            </span>
          </div>
          <div className="h-44 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={[]} margin={{ top: 5, right: 0, left: -25, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#525252" }} />
                <YAxis tick={{ fontSize: 10, fill: "#525252" }} />
                <Tooltip
                  contentStyle={{
                    fontSize: 12,
                    borderRadius: 8,
                    border: "1px solid #E5E7EB",
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="prospek" name="Prospek" fill="#E2231A" radius={[4, 4, 0, 0]} />
                <Bar dataKey="closing" name="Closing" fill="#1A1A1A" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Product distribution */}
      <div className="px-5 mt-4">
        <div className="honda-card p-4">
          <div className="flex items-center gap-2">
            <Target className="text-honda-red" size={16} />
            <p className="font-heading font-semibold text-sm text-honda-ink">
              Distribusi Produk Diminati
            </p>
          </div>
          <div className="flex items-center mt-3">
            <div className="w-32 h-32">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={productDistribution}
                    dataKey="value"
                    cx="50%"
                    cy="50%"
                    innerRadius={32}
                    outerRadius={56}
                    paddingAngle={2}
                  >
                    {productDistribution.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 grid grid-cols-2 gap-x-2 gap-y-1.5 ml-2">
              {productDistribution.slice(0, 6).map((p, i) => (
                <div key={p.name} className="flex items-center gap-1.5 text-[11px]">
                  <span
                    className="w-2 h-2 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: COLORS[i % COLORS.length] }}
                  />
                  <span className="text-honda-ink truncate">{p.name}</span>
                  <span className="text-honda-muted ml-auto">{p.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Leaderboard */}
      <div className="px-5 mt-4">
        <div className="honda-card p-4">
          <div className="flex items-center gap-2">
            <Award className="text-honda-red" size={16} />
            <p className="font-heading font-semibold text-sm text-honda-ink">
              Top Salesman Wilayah
            </p>
          </div>
          <div className="mt-3 space-y-2">
            {[{ id: user?.id, name: user?.name, dealer: user?.dealer, closing: closingMonth }].map((s, i) => {
              const isMe = s.id === user?.id;
              const medal = ["🥇", "🥈", "🥉"][i];
              return (
                <div
                  key={s.id}
                  data-testid={`leaderboard-row-${i}`}
                  className={`flex items-center gap-3 p-2.5 rounded-lg ${
                    isMe ? "bg-honda-red-light border border-honda-red/30" : "bg-honda-bg"
                  }`}
                >
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center font-heading font-bold text-xs ${
                      i < 3 ? "bg-white" : "bg-honda-border text-honda-muted"
                    }`}
                  >
                    {medal || `#${i + 1}`}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold truncate ${isMe ? "text-honda-red" : "text-honda-ink"}`}>
                      {s.name} {isMe && <span className="text-[10px]">(Anda)</span>}
                    </p>
                    <p className="text-[11px] text-honda-muted flex items-center gap-1">
                      <MapPin size={10} /> {s.dealer}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-heading font-bold text-sm text-honda-ink">
                      {s.closing}
                    </p>
                    <p className="text-[10px] text-honda-muted">closing</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
